/**
 * EvidenceDropZone
 * ================
 *
 * Reusable, context-agnostic drag-and-drop upload surface for evidence files.
 *
 * Feb 22, 2026 — Session 21 (Phase 1: Tax → Information Source)
 *
 * Design goals:
 *   • Single premium-SaaS upload pattern for the entire platform.
 *   • Zero hardcoded knowledge of Tax, Title, or any particular workflow.
 *   • All business rules (file type, size, min/max count, role permissions,
 *     backend upload endpoint) are injected via props — the component itself
 *     only handles: drag/drop ↔ click parity, client-side validation,
 *     progress state, uploaded-chip rendering, accessibility.
 *
 * Contract:
 *   <EvidenceDropZone
 *     label="Portal Screenshot"
 *     icon={Globe}
 *     helperText="Min: 1 portal screenshot"
 *     formatDisplay="PNG, JPG, PDF (max 15MB)"
 *     acceptString="image/png,image/jpeg,application/pdf,.png,.jpg,.jpeg,.pdf"
 *     acceptedMimeTypes={['image/png', 'image/jpeg', 'application/pdf']}
 *     acceptedExtensions={['.png', '.jpg', '.jpeg', '.pdf']}
 *     maxFiles={3}
 *     minRequired={1}
 *     maxSize={15 * 1024 * 1024}
 *     existingFiles={files}                       // [{ id, filename, url, ... }]
 *     uploadHandler={async (file) => { ... }}    // MUST return the uploaded file descriptor
 *     removeHandler={async (fileId) => { ... }}  // OPTIONAL; hides × if absent
 *     multiple                                   // default true
 *     disabled={false}
 *     viewOnly={false}
 *     testIdPrefix="tax-evidence-online"
 *   />
 *
 * The parent component OWNS `existingFiles` and is responsible for appending the
 * new file returned by `uploadHandler`. This keeps state flow one-way (lifted)
 * and lets multiple instances share a single source of truth (e.g. a
 * jurisdiction's `source_evidence` object keyed by evidence type).
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  UploadCloud,
  CheckCircle2,
  Loader2,
  X,
  AlertCircle,
  Eye,
  FileText,
  FileImage,
  FileAudio,
  File as FileIcon,
} from 'lucide-react';

// ─── File-icon helper ───────────────────────────────────────────────────────
const pickFileIcon = (filename = '') => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'heif', 'tif', 'tiff'].includes(ext)) return FileImage;
  if (['mp3', 'wav', 'm4a', 'ogg', 'aac', 'flac'].includes(ext)) return FileAudio;
  if (['pdf', 'doc', 'docx', 'txt', 'eml'].includes(ext)) return FileText;
  return FileIcon;
};

// ─── Byte formatter ─────────────────────────────────────────────────────────
const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ─── Image-thumbnail resolver ───────────────────────────────────────────────
// If the uploaded file is an image and we have a URL, show it as a thumb;
// otherwise fall back to a type-appropriate icon.
const isImageFile = (filename = '') =>
  /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(filename);

// ─── The component ──────────────────────────────────────────────────────────
const EvidenceDropZone = ({
  label,
  icon: Icon = UploadCloud,
  helperText,
  formatDisplay,
  acceptString,
  acceptedMimeTypes = [],
  acceptedExtensions = [],
  maxFiles = 3,
  minRequired = 0,
  maxSize = 15 * 1024 * 1024,
  existingFiles = [],
  uploadHandler,
  removeHandler,
  viewAction, // optional (file) => void — custom "Eye" action; falls back to file.url
  multiple = true,
  disabled = false,
  viewOnly = false,
  className = '',
  testIdPrefix = 'evidence-dropzone',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [validationError, setValidationError] = useState('');
  const inputRef = useRef(null);
  const dragCounterRef = useRef(0); // robust drag-leave tracking inside nested elements

  const fileCount = existingFiles.length;
  const remainingSlots = Math.max(0, maxFiles - fileCount);
  const atCapacity = remainingSlots === 0;
  const isUploading = uploadingCount > 0;
  const isInteractive = !disabled && !viewOnly && !atCapacity;
  const meetsMinimum = fileCount >= minRequired;

  // ── Validation ────────────────────────────────────────────────────────────
  const validateFile = useCallback(
    (file) => {
      const extension = '.' + (file.name.split('.').pop() || '').toLowerCase();
      const extensionOk =
        acceptedExtensions.length === 0 ||
        acceptedExtensions.map((e) => e.toLowerCase()).includes(extension);
      const mimeOk =
        acceptedMimeTypes.length === 0 ||
        acceptedMimeTypes.some(
          (t) => file.type === t || (t.endsWith('/*') && file.type.startsWith(t.slice(0, -1)))
        );

      if (!extensionOk && !mimeOk) {
        return `Invalid file type. Allowed: ${formatDisplay || acceptedExtensions.join(', ')}`;
      }
      if (file.size > maxSize) {
        return `"${file.name}" exceeds ${formatBytes(maxSize)} limit`;
      }
      return null;
    },
    [acceptedExtensions, acceptedMimeTypes, formatDisplay, maxSize]
  );

  // ── Core upload-queue runner ──────────────────────────────────────────────
  const ingestFiles = useCallback(
    async (fileList) => {
      setValidationError('');
      if (!fileList || fileList.length === 0) return;

      const files = Array.from(fileList);
      // Enforce "only `multiple=false` → take first" semantics early.
      const queued = multiple ? files : files.slice(0, 1);

      if (queued.length > remainingSlots) {
        setValidationError(
          `Can only add ${remainingSlots} more file${remainingSlots === 1 ? '' : 's'} (max ${maxFiles}).`
        );
        return;
      }

      for (const file of queued) {
        const err = validateFile(file);
        if (err) {
          setValidationError(err);
          return;
        }
      }

      if (!uploadHandler) {
        console.warn('EvidenceDropZone: uploadHandler prop is required');
        return;
      }

      // Per-batch tally used to drive a single, clear summary toast at the
      // end of the loop. See toast block after the loop for wording rules.
      const succeeded = [];
      const failed = [];

      for (const file of queued) {
        setUploadingCount((c) => c + 1);
        try {
          await uploadHandler(file);
          succeeded.push(file);
        } catch (e) {
          failed.push({ file, message: e?.message || `Failed to upload "${file.name}"` });
          setValidationError(e?.message || `Failed to upload "${file.name}"`);
        } finally {
          setUploadingCount((c) => Math.max(0, c - 1));
        }
      }

      // Feb 26, 2026 — Batch upload toast (B1 + B2 per user spec).
      // Replaces the old per-file "Uploaded (N/M)" toast that was both
      // misleading (always read "1/3" due to a stale closure in the parent
      // upload handler) and noisy. Single drop → file name; multi drop →
      // batch count. Partial-failure case surfaces success-of-total wording
      // while preserving the per-file error already shown via setValidationError.
      if (succeeded.length === 1 && failed.length === 0) {
        toast.success(`Uploaded ${succeeded[0].name}`);
      } else if (succeeded.length > 1 && failed.length === 0) {
        toast.success(`Uploaded ${succeeded.length} files successfully`);
      } else if (succeeded.length > 0 && failed.length > 0) {
        toast.success(`Uploaded ${succeeded.length} of ${succeeded.length + failed.length} files`);
      }

      // Files-tab parity (item 4): announce that this order's file list has
      // changed so any mounted FilesTab can re-fetch immediately. No-op if
      // nothing listens. We fire BOTH event names because the codebase has
      // two pre-existing listeners — FilesTab listens to `orderFilesChanged`
      // (added with this fix) and TaxCertificatePanel listens to
      // `filesChangedInOrder` (pre-existing). The latter is what refreshes
      // the `orderFiles` array that the evidence panel hydrates from after
      // a batch upload, so the new chips show up immediately without the
      // user having to click Save first.
      if (succeeded.length > 0) {
        try {
          window.dispatchEvent(new CustomEvent('orderFilesChanged', {
            detail: { action: 'upload', count: succeeded.length },
          }));
          window.dispatchEvent(new CustomEvent('filesChangedInOrder', {
            detail: { action: 'upload', count: succeeded.length },
          }));
        } catch (_) { /* noop */ }
      }
    },
    [multiple, remainingSlots, maxFiles, validateFile, uploadHandler]
  );

  // ── Drag handlers (counter-based, resilient to child bubbling) ────────────
  const onDragEnter = (e) => {
    if (!isInteractive) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer?.items?.length) setIsDragging(true);
  };
  const onDragOver = (e) => {
    if (!isInteractive) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };
  const onDragLeave = (e) => {
    if (!isInteractive) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragging(false);
  };
  const onDrop = (e) => {
    if (!isInteractive) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) ingestFiles(e.dataTransfer.files);
  };

  // ── Click-to-browse (parity with drop) ────────────────────────────────────
  const handleZoneClick = () => {
    if (!isInteractive) return;
    inputRef.current?.click();
  };
  const handleInputChange = (e) => {
    ingestFiles(e.target.files);
    // Reset so re-selecting the same file still fires `onChange`.
    if (inputRef.current) inputRef.current.value = '';
  };

  // ── Style tokens ──────────────────────────────────────────────────────────
  const zoneStateClass = useMemo(() => {
    if (isDragging) {
      return 'border-indigo-500 bg-indigo-50 shadow-[0_0_0_4px_rgba(99,102,241,0.08)] scale-[1.005]';
    }
    if (disabled || viewOnly) return 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-75';
    if (atCapacity) return 'border-amber-200 bg-amber-50 cursor-not-allowed';
    return 'border-[#c7d2fe] bg-[#f8faff] hover:border-indigo-400 hover:bg-indigo-50/60 cursor-pointer';
  }, [isDragging, disabled, viewOnly, atCapacity]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className={`w-full ${className}`}
      data-testid={`${testIdPrefix}-root`}
    >
      {/* Label row */}
      {label && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-slate-600" />}
            <span className="text-sm font-medium text-slate-800">{label}</span>
          </div>
          <div className="flex items-center gap-2">
            {minRequired > 0 && !meetsMinimum && (
              <span className="text-[11px] font-medium text-rose-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Min {minRequired} required
              </span>
            )}
            <span
              className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                atCapacity
                  ? 'bg-amber-100 text-amber-700'
                  : fileCount >= minRequired && fileCount > 0
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-slate-100 text-slate-600'
              }`}
              data-testid={`${testIdPrefix}-counter`}
              title={`${fileCount} of ${maxFiles} ${label ? label.toLowerCase() + ' ' : ''}files uploaded for this evidence section`}
              aria-label={`${fileCount} of ${maxFiles} files uploaded for this evidence section`}
            >
              {fileCount}/{maxFiles}
            </span>
          </div>
        </div>
      )}

      {/* Drop zone surface */}
      <div
        role="button"
        tabIndex={isInteractive ? 0 : -1}
        aria-label={label ? `Upload ${label.toLowerCase()}` : 'Upload files'}
        aria-disabled={!isInteractive}
        onClick={handleZoneClick}
        onKeyDown={(e) => {
          if (!isInteractive) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleZoneClick();
          }
        }}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`relative w-full rounded-[14px] border border-dashed transition-all duration-150 ease-out px-6 py-5 ${zoneStateClass}`}
        data-testid={`${testIdPrefix}-surface`}
        data-dragging={isDragging ? 'true' : 'false'}
      >
        <div className="flex items-center gap-4">
          {/* Big icon circle */}
          <div
            className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-colors ${
              isDragging ? 'bg-indigo-100 text-indigo-600' : 'bg-white text-indigo-500 border border-indigo-100'
            }`}
          >
            {isUploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <UploadCloud className="w-5 h-5" />
            )}
          </div>

          {/* Text block */}
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-slate-800">
              {atCapacity
                ? 'Maximum files reached'
                : isDragging
                ? 'Drop to upload'
                : 'Drag & drop files here'}
            </p>
            <p className="text-[12px] text-slate-500 mt-0.5">
              {atCapacity ? (
                <>Remove a file to add another</>
              ) : (
                <>
                  or <span className="text-indigo-600 font-medium">click to browse</span>
                </>
              )}
            </p>
            <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-400">
              {formatDisplay && <span>{formatDisplay}</span>}
              {helperText && formatDisplay && <span className="w-1 h-1 rounded-full bg-slate-300" />}
              {helperText && <span>{helperText}</span>}
            </div>
          </div>
        </div>

        {/* Hidden native input — covers click + picker */}
        <input
          ref={inputRef}
          type="file"
          accept={acceptString}
          multiple={multiple}
          onChange={handleInputChange}
          className="hidden"
          data-testid={`${testIdPrefix}-input`}
          disabled={!isInteractive}
        />
      </div>

      {/* Inline validation error */}
      {validationError && (
        <div
          className="mt-2 flex items-start gap-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700"
          data-testid={`${testIdPrefix}-error`}
        >
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{validationError}</span>
          <button
            type="button"
            onClick={() => setValidationError('')}
            className="ml-auto text-rose-500 hover:text-rose-700"
            aria-label="Dismiss error"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Uploaded file chips */}
      {existingFiles.length > 0 && (
        <ul
          className="mt-3 space-y-2"
          data-testid={`${testIdPrefix}-file-list`}
        >
          {existingFiles.map((file, idx) => {
            const TypeIcon = pickFileIcon(file.filename);
            const showThumb = isImageFile(file.filename) && file.url;
            return (
              <li
                key={file.id || `${file.filename}-${idx}`}
                className="group flex items-center gap-3 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all px-3 py-2"
                data-testid={`${testIdPrefix}-file-${file.id || idx}`}
              >
                {/* Thumb / icon */}
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden">
                  {showThumb ? (
                    <img
                      src={file.url}
                      alt={file.filename || 'Uploaded file preview'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to icon if the thumbnail URL fails to load.
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <TypeIcon className="w-4 h-4 text-slate-500" />
                  )}
                </div>

                {/* Name + status */}
                <div className="flex-1 min-w-0">
                  <p
                    className="text-[13px] font-medium text-slate-800 truncate"
                    title={file.filename}
                  >
                    {file.filename || 'Untitled file'}
                  </p>
                  <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                    <span className="inline-flex items-center gap-1 text-emerald-600">
                      <CheckCircle2 className="w-3 h-3" />
                      Uploaded
                    </span>
                    {file.size ? <span>{formatBytes(file.size)}</span> : null}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                  {(viewAction || file.url) && (
                    <button
                      type="button"
                      className="p-1.5 rounded-md text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (viewAction) viewAction(file);
                        else if (file.url) window.open(file.url, '_blank', 'noopener');
                      }}
                      title="View"
                      data-testid={`${testIdPrefix}-view-${file.id || idx}`}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                  {!viewOnly && removeHandler && (
                    <button
                      type="button"
                      className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await removeHandler(file.id);
                        } catch (err) {
                          setValidationError(err?.message || 'Failed to remove file');
                        }
                      }}
                      title="Remove"
                      data-testid={`${testIdPrefix}-remove-${file.id || idx}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default EvidenceDropZone;
export { formatBytes };
